/**
 * SAB-985 — human copy for auth failures, instead of echoing the raw error.
 *
 * A sign-in failure on 2026-09-01 rendered:
 *
 *   "Sign in failed: Load failed (fndnsflniialrkopxcnr.supabase.co)"
 *
 * Three problems in one toast. It leaked our Supabase project hostname to a user. "Load
 * failed" is WebKit's wording for "the request never completed" and means nothing to a
 * person. And it was indistinguishable from a wrong password, so the user could not tell
 * whether to fix their connection or their credentials — prod was healthy throughout; his
 * own connection had dropped the request, and good copy would have told him so.
 *
 * ── THE SAFETY PROPERTY IS STRUCTURAL, NOT A FILTER ─────────────────────────
 *
 * This module NEVER returns any part of the underlying error. It classifies and returns a
 * fixed string. There is deliberately no "sanitise the message" path, because a redactor
 * is only as good as its pattern list and the thing being redacted is attacker-adjacent
 * infrastructure detail. The raw error goes to `detail`, for logging only.
 *
 * Callers: render `title`/`description`, and log `detail`. Never render `detail`.
 */

export type AuthAction = 'signin' | 'signup' | 'reset' | 'resend' | 'oauth' | 'update_password';

export interface AuthErrorCopy {
  title: string;
  description: string;
  /** The raw text, for console/Sentry ONLY. Never rendered. */
  detail: string;
  /** Which class matched, so tests and logs can assert the branch. */
  kind: 'network' | 'credentials' | 'unconfirmed' | 'rate_limit' | 'already_registered' | 'weak_password' | 'unknown';
}

const GENERIC: Record<AuthAction, string> = {
  signin: 'Something went wrong signing you in. Try again in a moment.',
  signup: 'Something went wrong creating your account. Try again in a moment.',
  reset:  'Something went wrong sending your reset link. Try again in a moment.',
  resend: 'Something went wrong resending your email. Try again in a moment.',
  oauth:  'Something went wrong with that sign-in. Try again in a moment.',
  // Setting a new password from a reset link -- NOT the same as sending the link, and the
  // copy has to say so or the user re-requests an email they already have.
  update_password: 'Something went wrong updating your password. Try again in a moment.',
};

const TITLE: Record<AuthAction, string> = {
  signin: 'Sign in failed',
  signup: 'Sign up failed',
  reset:  'Reset failed',
  resend: 'Resend failed',
  oauth:  'Sign in failed',
  update_password: 'Password not updated',
};

/** Everything a browser calls "the request did not complete". */
function isNetwork(msg: string, status: number | undefined, name: string): boolean {
  // status 0 / absent is what a fetch rejection surfaces; a real API error carries 4xx/5xx.
  if (name === 'AuthRetryableFetchError') return true;
  if (name === 'AbortError' || name === 'TimeoutError') return true;
  return (
    /load failed/i.test(msg) ||            // WebKit / iOS Safari — the reported case
    /failed to fetch/i.test(msg) ||        // Chromium
    /networkerror/i.test(msg) ||           // Firefox
    /network request failed/i.test(msg) || // React Native
    /timed? ?out/i.test(msg) ||
    /err_internet_disconnected/i.test(msg)
  ) && (status === undefined || status === 0);
}

export function authErrorCopy(err: unknown, action: AuthAction): AuthErrorCopy {
  const e = (err ?? {}) as { message?: unknown; status?: unknown; name?: unknown; code?: unknown };
  const msg = typeof e.message === 'string' ? e.message : String(e.message ?? '');
  const status = typeof e.status === 'number' ? e.status : undefined;
  const name = typeof e.name === 'string' ? e.name : '';
  const code = typeof e.code === 'string' ? e.code : '';
  const detail = `${name || 'Error'}${status !== undefined ? ` ${status}` : ''}: ${msg || '(no message)'}`;

  const copy = (kind: AuthErrorCopy['kind'], description: string): AuthErrorCopy =>
    ({ title: TITLE[action], description, detail, kind });

  // Network first: on iOS this is the single most common real cause, and it is the one the
  // old copy made indistinguishable from a bad password.
  if (isNetwork(msg, status, name)) {
    return copy('network', "Can't reach Sabotage Fantasy. Check your connection and try again.");
  }

  if (status === 429 || /rate limit/i.test(msg) || /you can only request this after/i.test(msg) ||
      code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit') {
    return copy('rate_limit', 'Too many attempts. Wait a minute and try again.');
  }

  if (/invalid login credentials/i.test(msg) || code === 'invalid_credentials' || /invalid_grant/i.test(msg)) {
    return copy('credentials', 'Email or password is incorrect.');
  }

  if (/email not confirmed/i.test(msg) || code === 'email_not_confirmed') {
    return copy('unconfirmed', 'Confirm your email first — check your inbox for the link.');
  }

  if (/already registered/i.test(msg) || /already exists/i.test(msg) || code === 'user_already_exists') {
    return copy('already_registered', 'That email is already registered. Try signing in instead.');
  }

  if (/password should be at least/i.test(msg) || code === 'weak_password') {
    return copy('weak_password', 'Choose a longer password — at least 6 characters.');
  }

  return copy('unknown', GENERIC[action]);
}
